import { isSameSession, loadSession, type AuthSession } from "./authApi";
import { apiFetch } from "./apiClient";
import { Role } from "@/types/auth";

export type PushNotificationErrorCode =
  | "NOT_SUPPORTED"
  | "VAPID_NOT_CONFIGURED"
  | "SERVICE_WORKER_ERROR"
  | "BACKEND_ERROR"
  | "PERMISSION_DENIED"
  | "PWA_NOT_INSTALLED"
  | "UNKNOWN";

export class PushNotificationError extends Error {
  code: PushNotificationErrorCode;

  constructor(message: string, code: PushNotificationErrorCode) {
    super(message);
    this.name = "PushNotificationError";
    this.code = code;
  }
}

export interface PushSupportCapabilities {
  hasServiceWorker: boolean;
  hasPushManager: boolean;
  hasNotification: boolean;
  isIOS: boolean;
  isPWAInstalled: boolean;
  isIOSPushSupported: boolean;
}

export type PushSupportCode =
  | "SUPPORTED"
  | "NOT_SUPPORTED"
  | "PWA_NOT_INSTALLED"
  | "IOS_VERSION_UNSUPPORTED";

export interface PushSupportState {
  isSupported: boolean;
  code: PushSupportCode;
  message: string;
}

export interface BackendSubscriptionStatus {
  subscribed: boolean;
  device_subscribed: boolean;
  subscription_count: number;
  active_subscription_count: number;
  endpoints: string[];
}

export interface ResolvedSubscriptionStatus {
  isSubscribed: boolean;
  browserSubscribed: boolean;
  backendSubscribed: boolean;
  deviceSubscribed: boolean;
  synced: boolean;
  needsServerSync: boolean;
  hasStaleServerDevices: boolean;
}

export interface PushDiagnostics {
  userAgent: string;
  isIOS: boolean;
  iosVersion: string | null;
  isPWAInstalled: boolean;
  isStandalone: boolean;
  hasServiceWorker: boolean;
  hasPushManager: boolean;
  hasNotification: boolean;
  serviceWorkerState: string;
  serviceWorkerScope: string | null;
  notificationPermission: string;
  hasSubscription: boolean;
  subscriptionEndpoint: string | null;
  backendSubscribed: boolean;
  backendSubscriptionCount: number;
  deviceSubscribed: boolean;
  vapidConfigured: boolean;
  lastError: string | null;
}

let cachedVapidPublicKey: string | null = null;
let pendingVapidRequest: Promise<string> | null = null;
let pendingVapidSession: string | null = null;
let pendingVapidIdentity: symbol | null = null;
let cachedBackendStatus: BackendSubscriptionStatus | null = null;
let cachedBackendEndpoint: string | null = null;
let cachedBackendSession: string | null = null;
let lastBackendStatusCheck = 0;
let pendingStatusPromise: Promise<BackendSubscriptionStatus | null> | null = null;
let pendingStatusKey: string | null = null;
let pendingStatusIdentity: symbol | null = null;
let lastPushError: string | null = null;
let pendingEnrollment: {
  sessionId: string;
  promise: Promise<PushSubscription>;
} | null = null;

const BACKEND_STATUS_CACHE_MS = 30000;
const AUTO_ENROLLMENT_DISABLED_KEY = "chauvin.push.auto-enrollment-disabled";
let autoEnrollmentDisabledSession: string | null = null;
let receiptRetryTimer: number | undefined;
let pendingReceiptFlush = false;

function pushSessionKey(session: AuthSession): string {
  return `${session.user.id}:${session.sessionId}`;
}

function automaticEnrollmentDisabled(session: AuthSession): boolean {
  const sessionKey = pushSessionKey(session);
  if (autoEnrollmentDisabledSession === sessionKey) return true;
  try {
    return localStorage.getItem(AUTO_ENROLLMENT_DISABLED_KEY) === sessionKey;
  } catch {
    return false;
  }
}

function setAutomaticEnrollmentDisabled(session: AuthSession, disabled: boolean): void {
  if (!isSameSession(session)) return;
  autoEnrollmentDisabledSession = disabled ? pushSessionKey(session) : null;
  try {
    if (disabled) localStorage.setItem(AUTO_ENROLLMENT_DISABLED_KEY, pushSessionKey(session));
    else localStorage.removeItem(AUTO_ENROLLMENT_DISABLED_KEY);
  } catch {
    // In-memory suppression still protects other mounted hooks in this tab.
  }
}

export function getPushSupportState(
  capabilities: PushSupportCapabilities,
): PushSupportState {
  if (
    !capabilities.hasServiceWorker ||
    !capabilities.hasPushManager ||
    !capabilities.hasNotification
  ) {
    return {
      isSupported: false,
      code: "NOT_SUPPORTED",
      message: "Les notifications push ne sont pas supportées par ce navigateur.",
    };
  }

  if (capabilities.isIOS && !capabilities.isPWAInstalled) {
    return {
      isSupported: false,
      code: "PWA_NOT_INSTALLED",
      message:
        "L'application doit être installée sur l'écran d'accueil pour activer les notifications.",
    };
  }

  if (capabilities.isIOS && !capabilities.isIOSPushSupported) {
    return {
      isSupported: false,
      code: "IOS_VERSION_UNSUPPORTED",
      message: "Les notifications push nécessitent iOS 16.4 ou supérieur.",
    };
  }

  return {
    isSupported: true,
    code: "SUPPORTED",
    message: "Notifications push disponibles.",
  };
}

export function resolveSubscriptionStatus(
  browserSubscribed: boolean,
  backendStatus: BackendSubscriptionStatus | null,
): ResolvedSubscriptionStatus {
  const backendSubscribed = backendStatus?.subscribed ?? false;
  const deviceSubscribed = backendStatus?.device_subscribed ?? false;
  const isSubscribed = browserSubscribed && deviceSubscribed;
  const hasStaleServerDevices = !browserSubscribed && backendSubscribed;

  return {
    isSubscribed,
    browserSubscribed,
    backendSubscribed,
    deviceSubscribed,
    synced: browserSubscribed === deviceSubscribed && !hasStaleServerDevices,
    needsServerSync: browserSubscribed && !deviceSubscribed,
    hasStaleServerDevices,
  };
}

export function isIOSDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return (
    /Macintosh/.test(ua) &&
    navigator.maxTouchPoints > 1 &&
    /Safari/.test(ua) &&
    !/Chrome/.test(ua)
  );
}

function getIOSVersion(): { major: number; minor: number } | null {
  if (typeof navigator === "undefined") return null;
  const match = navigator.userAgent.match(/OS (\d+)_(\d+)/);
  if (match) {
    return { major: Number(match[1]), minor: Number(match[2]) };
  }
  if (isIOSDevice() && "PushManager" in window) {
    return { major: 16, minor: 4 };
  }
  return null;
}

function isIOSPushSupported(): boolean {
  if (!isIOSDevice()) return true;
  return "PushManager" in window;
}

export function isPWAInstalled(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function getCurrentCapabilities(): PushSupportCapabilities {
  return {
    hasServiceWorker: "serviceWorker" in navigator,
    hasPushManager: "PushManager" in window,
    hasNotification: "Notification" in window,
    isIOS: isIOSDevice(),
    isPWAInstalled: isPWAInstalled(),
    isIOSPushSupported: isIOSPushSupported(),
  };
}

export function isPushNotificationSupported(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }
  return getPushSupportState(getCurrentCapabilities()).isSupported;
}

export function getNotificationPermission(): NotificationPermission {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "default";
  }
  return Notification.permission;
}

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!("Notification" in window)) {
    throw new PushNotificationError(
      "Notifications non supportées",
      "NOT_SUPPORTED",
    );
  }
  return Notification.requestPermission();
}

function hasSession(): boolean {
  return loadSession() !== null;
}

function assertSession(expected?: AuthSession | null): void {
  if (!hasSession() || (expected && !isSameSession(expected))) {
    throw new PushNotificationError(
      "Session utilisateur introuvable",
      "BACKEND_ERROR",
    );
  }
}

function isVapidConfigured(): boolean {
  return Boolean(cachedVapidPublicKey);
}

export async function fetchVapidPublicKey(forceRefresh = false): Promise<string> {
  if (!forceRefresh && cachedVapidPublicKey) return cachedVapidPublicKey;
  const session = loadSession();
  assertSession(session);
  const sessionKey = `${session!.user.id}:${session!.sessionId}`;
  if (pendingVapidRequest && pendingVapidSession === sessionKey) return pendingVapidRequest;

  const identity = Symbol("push-vapid");
  pendingVapidIdentity = identity;
  pendingVapidSession = sessionKey;
  pendingVapidRequest = (async () => {
    assertSession(session);

    // apiFetch refreshes an expiring access token and retries once on 401, so
    // an expired token does not surface as a missing VAPID configuration.
    const response = await apiFetch("/notifications/vapid-public-key/");
    const data = await response.json().catch(() => ({}));

    if (!response.ok || !data.publicKey) {
      setLastPushError(data.error || `VAPID indisponible (${response.status})`);
      throw new PushNotificationError(
        "Configuration des notifications incomplète",
        "VAPID_NOT_CONFIGURED",
      );
    }

    const publicKey = String(data.publicKey);
    cachedVapidPublicKey = publicKey;
    return publicKey;
  })().finally(() => {
    if (pendingVapidIdentity === identity) {
      pendingVapidRequest = null;
      pendingVapidSession = null;
      pendingVapidIdentity = null;
    }
  });

  return pendingVapidRequest;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

async function waitForServiceWorker(): Promise<ServiceWorkerRegistration> {
  if (!("serviceWorker" in navigator)) {
    throw new PushNotificationError(
      "Service worker non supporté",
      "SERVICE_WORKER_ERROR",
    );
  }

  let timeoutId: number | undefined;
  try {
    return await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<ServiceWorkerRegistration>((_, reject) => {
        timeoutId = window.setTimeout(
          () => reject(new Error("Délai d'attente du service worker dépassé")),
          10000,
        );
      }),
    ]);
  } finally {
    window.clearTimeout(timeoutId);
  }
}

async function getCurrentSubscription(): Promise<PushSubscription | null> {
  if (!isPushNotificationSupported()) return null;
  try {
    const registration = await waitForServiceWorker();
    return registration.pushManager.getSubscription();
  } catch {
    return null;
  }
}

async function sendSubscriptionToBackend(
  subscription: PushSubscription,
): Promise<void> {
  assertSession();

  const subscriptionJson = subscription.toJSON();
  const payload = {
    endpoint: subscriptionJson.endpoint,
    keys: {
      p256dh: subscriptionJson.keys?.p256dh,
      auth: subscriptionJson.keys?.auth,
    },
  };

  if (!payload.endpoint || !payload.keys.p256dh || !payload.keys.auth) {
    throw new PushNotificationError(
      "Abonnement push incomplet",
      "SERVICE_WORKER_ERROR",
    );
  }

  const response = await apiFetch("/notifications/subscribe/", {
    method: "POST",
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const detail = await response.text();
    setLastPushError(`Serveur: ${detail || response.status}`);
    throw new PushNotificationError(
      "Impossible d'enregistrer l'abonnement sur le serveur",
      "BACKEND_ERROR",
    );
  }

  clearBackendStatusCache();
}

export function subscribeToPushNotifications(
  options: { automatic?: boolean } = {},
): Promise<PushSubscription> {
  const session = loadSession();
  if (!session) return Promise.reject(new PushNotificationError(
    "Session utilisateur introuvable", "BACKEND_ERROR",
  ));
  const sessionId = pushSessionKey(session);
  if (pendingEnrollment?.sessionId === sessionId) return pendingEnrollment.promise;
  const promise = enrollPushSubscription(session).then((subscription) => {
    if (!options.automatic) setAutomaticEnrollmentDisabled(session, false);
    return subscription;
  }).finally(() => {
    if (pendingEnrollment?.promise === promise) pendingEnrollment = null;
  });
  pendingEnrollment = { sessionId, promise };
  return promise;
}

async function enrollPushSubscription(session: AuthSession): Promise<PushSubscription> {
  assertSession(session);
  const support = getPushSupportState(getCurrentCapabilities());
  if (!support.isSupported) {
    throw new PushNotificationError(
      support.message,
      support.code === "PWA_NOT_INSTALLED" ? "PWA_NOT_INSTALLED" : "NOT_SUPPORTED",
    );
  }

  if (getNotificationPermission() !== "granted") {
    throw new PushNotificationError(
      "Autorisez les notifications avant l'activation.",
      "PERMISSION_DENIED",
    );
  }

  const vapidPublicKey = await fetchVapidPublicKey();
  const registration = await waitForServiceWorker();
  const existingSubscription = await registration.pushManager.getSubscription();
  assertSession(session);

  if (existingSubscription) {
    await sendSubscriptionToBackend(existingSubscription);
    return existingSubscription;
  }

  let subscription: PushSubscription;
  try {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    setLastPushError(`PushManager.subscribe: ${message}`);
    throw new PushNotificationError(
      `Échec de l'inscription: ${message}`,
      "PERMISSION_DENIED",
    );
  }

  try {
    assertSession(session);
    await sendSubscriptionToBackend(subscription);
    return subscription;
  } catch (error) {
    // A later sign-in may already be using this browser subscription. Never
    // detach their device because an older enrollment request finished late.
    if (session && isSameSession(session)) await subscription.unsubscribe();
    throw error;
  }
}

export async function unsubscribeFromPushNotifications(): Promise<boolean> {
  const session = loadSession();
  if (session) setAutomaticEnrollmentDisabled(session, true);
  try {
    // Finish an automatic enrollment already started by another mounted hook
    // before detaching its subscription, so an explicit opt-out wins.
    if (session && pendingEnrollment?.sessionId === pushSessionKey(session)) {
      await pendingEnrollment.promise.catch(() => undefined);
      setAutomaticEnrollmentDisabled(session, true);
    }
    const registration = await waitForServiceWorker();
    const subscription = await registration.pushManager.getSubscription();

    if (session) assertSession(session);
    else if (loadSession()) return false;

    if (!subscription) return true;

    const subscriptionJson = subscription.toJSON();
    const unsubscribed = await subscription.unsubscribe();
    if (!unsubscribed) return false;

    if (session && isSameSession(session)) {
      await apiFetch("/notifications/unsubscribe/", {
        method: "DELETE",
        body: JSON.stringify({ endpoint: subscriptionJson.endpoint }),
      });
    }

    clearBackendStatusCache();
    return true;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    setLastPushError(message);
    return false;
  }
}

const SIGN_OUT_UNSUBSCRIBE_TIMEOUT_MS = 4000;

/**
 * Detach this device from the current user's push subscriptions.
 *
 * Meant to run right before the session is cleared (the backend call needs a
 * valid token) so that on a shared phone the next employee does not receive
 * the previous one's notifications. Never throws and never blocks for more
 * than a few seconds: logout must always succeed.
 */
export async function unsubscribeDeviceOnSignOut(): Promise<boolean> {
  if (!isPushNotificationSupported()) {
    clearBackendStatusCache();
    return true;
  }

  let timeoutId: number | undefined;
  const timeout = new Promise<boolean>((resolve) => {
    timeoutId = window.setTimeout(() => resolve(false), SIGN_OUT_UNSUBSCRIBE_TIMEOUT_MS);
  });

  try {
    return await Promise.race([unsubscribeFromPushNotifications(), timeout]);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    setLastPushError(message);
    return false;
  } finally {
    window.clearTimeout(timeoutId);
    clearBackendStatusCache();
    cachedVapidPublicKey = null;
  }
}

function clearBackendStatusCache(): void {
  cachedBackendStatus = null;
  cachedBackendEndpoint = null;
  cachedBackendSession = null;
  lastBackendStatusCheck = 0;
  pendingStatusPromise = null;
  pendingStatusKey = null;
  pendingStatusIdentity = null;
}

async function checkBackendSubscriptionStatus(
  endpoint?: string | null,
  forceRefresh = false,
): Promise<BackendSubscriptionStatus | null> {
  const now = Date.now();
  const session = loadSession();
  if (!session) return null;
  const sessionKey = `${session.user.id}:${session.sessionId}`;
  const requestKey = `${sessionKey}:${endpoint || ""}`;
  const cacheMatches = cachedBackendEndpoint === (endpoint || null);
  if (
    !forceRefresh &&
    cachedBackendStatus &&
    cachedBackendSession === sessionKey &&
    cacheMatches &&
    now - lastBackendStatusCheck < BACKEND_STATUS_CACHE_MS
  ) {
    return cachedBackendStatus;
  }

  if (pendingStatusPromise && pendingStatusKey === requestKey) return pendingStatusPromise;

  pendingStatusKey = requestKey;
  const requestIdentity = Symbol("push-status");
  pendingStatusIdentity = requestIdentity;
  const requestPromise = (async () => {
    const params = new URLSearchParams();
    if (endpoint) params.set("endpoint", endpoint);
    const suffix = params.toString() ? `?${params.toString()}` : "";

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 5000);

    try {
      const response = await apiFetch(`/notifications/status/${suffix}`, {
        signal: controller.signal,
      });

      if (!response.ok) return null;

      const data = (await response.json()) as BackendSubscriptionStatus;
      if (!isSameSession(session)) return null;
      cachedBackendStatus = data;
      cachedBackendEndpoint = endpoint || null;
      cachedBackendSession = sessionKey;
      lastBackendStatusCheck = Date.now();
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setLastPushError(message);
      return null;
    } finally {
      window.clearTimeout(timeoutId);
      if (pendingStatusIdentity === requestIdentity) {
        pendingStatusPromise = null;
        pendingStatusKey = null;
        pendingStatusIdentity = null;
      }
    }
  })();
  pendingStatusPromise = requestPromise;

  return requestPromise;
}

/** Wake the receipt outbox without exposing user tokens to the worker. */
export async function flushPendingPushReceipts(): Promise<void> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  if (pendingReceiptFlush) return;
  window.clearTimeout(receiptRetryTimer);
  receiptRetryTimer = undefined;
  pendingReceiptFlush = true;
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    const worker = navigator.serviceWorker.controller || registration?.active;
    if (!worker) {
      pendingReceiptFlush = false;
      return;
    }
    const message = { type: "ODILON_FLUSH_PUSH_RECEIPTS" };
    if (!("MessageChannel" in window)) {
      worker.postMessage(message);
      pendingReceiptFlush = false;
      return;
    }
    const channel = new MessageChannel();
    let settled = false;
    const finish = (remaining: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      channel.port1.close();
      channel.port2.close();
      pendingReceiptFlush = false;
      // Safari has no Background Sync. Drain a backlog in short worker wakes
      // while the portal is visible, stopping as soon as the outbox is empty.
      if (remaining && navigator.onLine !== false && document.visibilityState === "visible") {
        receiptRetryTimer = window.setTimeout(() => {
          if (navigator.onLine !== false && document.visibilityState === "visible") {
            void flushPendingPushReceipts();
          }
        }, 15000);
      }
    };
    const timeout = window.setTimeout(() => finish(false), 30000);
    channel.port1.onmessage = (event: MessageEvent<{ remaining?: boolean }>) => {
      finish(event.data?.remaining === true);
    };
    try {
      worker.postMessage(message, [channel.port2]);
    } catch {
      finish(false);
    }
  } catch {
    pendingReceiptFlush = false;
    // Browser receipt reporting must never prevent using the portal.
  }
}

export async function getFullSubscriptionStatus(): Promise<
  ResolvedSubscriptionStatus & {
    backendStatus: BackendSubscriptionStatus | null;
  }
> {
  const subscription = await getCurrentSubscription();
  const endpoint = subscription?.endpoint ?? null;
  const backendStatus = await checkBackendSubscriptionStatus(endpoint);
  return {
    ...resolveSubscriptionStatus(Boolean(subscription), backendStatus),
    backendStatus,
  };
}

/** Reconcile on visits and browser lifecycle events, without a render retry loop. */
export async function refreshPushSubscription() {
  const session = loadSession();
  assertSession(session);
  let status = await getFullSubscriptionStatus();
  assertSession(session);
  if (
    session && !automaticEnrollmentDisabled(session) &&
    getNotificationPermission() === "granted" &&
    (status.needsServerSync || (!status.browserSubscribed && session.user.role === Role.ADMIN))
  ) {
    await subscribeToPushNotifications({ automatic: true });
    status = await getFullSubscriptionStatus();
    assertSession(session);
  }
  return status;
}

function setLastPushError(error: string | null): void {
  lastPushError = error;
}

export async function getPushDiagnostics(): Promise<PushDiagnostics> {
  const ua = navigator.userAgent;
  const isIOS = isIOSDevice();
  const version = getIOSVersion();
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  const hasServiceWorker = "serviceWorker" in navigator;
  const hasPushManager = "PushManager" in window;
  const hasNotification = "Notification" in window;

  let serviceWorkerState = "not_registered";
  let serviceWorkerScope: string | null = null;
  if (hasServiceWorker) {
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        serviceWorkerScope = registration.scope;
        if (registration.active) serviceWorkerState = "active";
        else if (registration.installing) serviceWorkerState = "installing";
        else if (registration.waiting) serviceWorkerState = "waiting";
      }
    } catch (error) {
      serviceWorkerState = `error: ${String(error)}`;
    }
  }

  const subscription = await getCurrentSubscription();
  const endpoint = subscription?.endpoint ?? null;
  const backendStatus = await checkBackendSubscriptionStatus(endpoint, true);

  return {
    userAgent: ua.substring(0, 100) + (ua.length > 100 ? "..." : ""),
    isIOS,
    iosVersion: version ? `${version.major}.${version.minor}` : null,
    isPWAInstalled: isPWAInstalled(),
    isStandalone,
    hasServiceWorker,
    hasPushManager,
    hasNotification,
    serviceWorkerState,
    serviceWorkerScope,
    notificationPermission: hasNotification ? Notification.permission : "unknown",
    hasSubscription: Boolean(subscription),
    subscriptionEndpoint: endpoint ? `${endpoint.substring(0, 60)}...` : null,
    backendSubscribed: backendStatus?.subscribed ?? false,
    backendSubscriptionCount: backendStatus?.active_subscription_count ?? 0,
    deviceSubscribed: backendStatus?.device_subscribed ?? false,
    vapidConfigured: isVapidConfigured(),
    lastError: lastPushError,
  };
}
